/**
 * R4 — Source unique de vérité pour la classification politique.
 *
 * Avant le refactor, `ProfileCrystalOverlay::classifyProfile` (T8) et
 * `MandateEndOverlay::classifySignature` (T60) dupliquaient ~200 LoC
 * de logique de matching (12+ branchements if/else sur les 4 axes
 * eco/soc/intl/auth). Tout changement nécessitait de modifier les
 * deux endroits, avec un risque de divergence silencieuse.
 *
 * Désormais, `classifyArchetypeKey` retourne un identifiant stable
 * basé uniquement sur la silhouette politique. Chaque composant
 * importe sa propre table de *contenu* (quotes T8 ou closings T60).
 * La logique de classification reste indivisée.
 */

import type { PoliticalProfile } from "./types";

/** Identifiants stables des archétypes politiques (13 + composite + centriste). */
export type ArchetypeKey =
  | "centriste_assume"
  | "neoconservateur"
  | "conservateur_liberal"
  | "progressiste_autoritaire"
  | "sociale_democrate"
  | "ecologiste_pragmatique"
  | "souverainiste_gauche"
  | "national_populiste"
  | "populiste_gouvernement"
  | "libertaire_droite"
  | "liberal_classique"
  | "etatiste_reformateur"
  | "tenant_ordre"
  | "souverainiste"
  | "profil_composite";

const AXIS_THRESHOLD = 0.1;

/**
 * Classe le profil en archétype politique. Cascade d'instinctifs
 * priorisée comme avant : combinaisons signature à 3 axes en premier,
 * puis 2 axes, puis fallback monoaxial. Cohérence garantie entre les
 * overlays T8 et T60.
 */
export function classifyArchetypeKey(p: PoliticalProfile): ArchetypeKey {
  const eco = p.economic;
  const soc = p.societal;
  const intl = p.international;
  const aut = p.authority;

  const maxAbs = Math.max(
    Math.abs(eco),
    Math.abs(soc),
    Math.abs(intl),
    Math.abs(aut),
  );
  if (maxAbs < AXIS_THRESHOLD) return "centriste_assume";

  const ecoLib = eco > AXIS_THRESHOLD;
  const ecoSoc = eco < -AXIS_THRESHOLD;
  const socProg = soc < -AXIS_THRESHOLD;
  const socCons = soc > AXIS_THRESHOLD;
  const intlSouv = intl > AXIS_THRESHOLD;
  const intlMond = intl < -AXIS_THRESHOLD;
  const autFort = aut > AXIS_THRESHOLD;
  const autLib = aut < -AXIS_THRESHOLD;

  if (ecoLib && socCons && intlSouv) return "neoconservateur";
  if (ecoLib && socCons && autFort) return "conservateur_liberal";
  if (ecoSoc && socProg && autFort) return "progressiste_autoritaire";
  if (ecoSoc && socProg && intlMond) return "sociale_democrate";
  if (ecoSoc && socProg && autLib) return "ecologiste_pragmatique";
  // Fix 2026-06-12 (bug playtest) : l'ancienne condition exigeait
  // socCons — un joueur d'extrême droite (économie protectionniste +
  // conservateur + souverainiste = silhouette national-populiste) se
  // voyait étiqueté « de gauche ». Le vrai souverainisme de gauche
  // (ligne Chevènement/Frexit social) est PROGRESSISTE sur l'axe
  // sociétal ; la silhouette conservatrice a son propre bucket.
  if (ecoSoc && socProg && intlSouv) return "souverainiste_gauche";
  if (ecoSoc && socCons && intlSouv) return "national_populiste";
  if (intlSouv && socCons && autFort) return "populiste_gouvernement";
  if (ecoLib && socProg && autLib) return "libertaire_droite";
  if (ecoLib && socProg) return "liberal_classique";
  if (ecoSoc && autFort) return "etatiste_reformateur";
  if (autFort) return "tenant_ordre";
  if (intlSouv) return "souverainiste";

  return "profil_composite";
}

/** Libellés courts des archétypes (commun aux 2 overlays). */
export const ARCHETYPE_NAME: Record<ArchetypeKey, string> = {
  centriste_assume: "Centriste assumé",
  neoconservateur: "Néoconservateur",
  conservateur_liberal: "Conservateur libéral",
  progressiste_autoritaire: "Progressiste autoritaire",
  sociale_democrate: "Sociale-démocrate",
  ecologiste_pragmatique: "Écologiste pragmatique",
  souverainiste_gauche: "Souverainiste de gauche",
  national_populiste: "National-populiste",
  populiste_gouvernement: "Populiste de gouvernement",
  libertaire_droite: "Libertaire de droite",
  liberal_classique: "Libéral classique",
  etatiste_reformateur: "Étatiste réformateur",
  tenant_ordre: "Tenant de l'ordre",
  souverainiste: "Souverainiste",
  profil_composite: "Profil composite",
};
